import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import { company } from '~/company/Company';
import { CompaniesMap } from '~/company/companiesMap';
import type { CompanyName } from '~/company/interface';
import { checkoutStore, mainStore, userStore } from '~/stores';
import { deleteCookie, getCookie, setCookie } from '~/utils/cookies';

import { deliveryAddressStore } from '../ModalDeliveryAddress/DeliveryAddressStore';

import styles from './styles.module.scss';

type CompanyRadioItemData = {
  name: CompanyName;
  original: boolean;
};

const TURNSTILE_TEST_KEYS: Array<{ key: string; label: string }> = [
  { key: '1x00000000000000000000AA', label: 'always passes' },
  { key: '2x00000000000000000000AB', label: 'always blocks' },
  { key: '3x00000000000000000000FF', label: 'always interactive' },
];

const CAPTCHA_DEV_SITE_KEY_STORAGE = 'captcha-dev-site-key';

const RadioList = ({
  companies,
}: {
  companies: Array<CompanyRadioItemData>;
}) => {
  const [selectedCompany, setSelectedCompany] = useState<{
    original: boolean;
    name: CompanyName;
  } | null>(null);

  useEffect(() => {
    setSelectedCompany({
      original: true,
      name: company.name,
    });
  }, []);

  useEffect(() => {
    if (getCookie('selectedCompany')) {
      setSelectedCompany(getCookie('selectedCompany'));
    }
  }, []);

  const handleSelect = (data: CompanyRadioItemData) => {
    setCookie('selectedCompany', JSON.stringify(data));

    if (
      data.original !== selectedCompany?.original ||
      (data.original && data.name !== selectedCompany.name)
    ) {
      deliveryAddressStore.resetStore();
      checkoutStore.resetStore({
        resetDeliveryMethod: true,
      });
      userStore.resetStore();
      localStorage.clear();
      sessionStorage.clear();
      deleteCookie('warehouse');
    }

    mainStore.sendToRN('changeCompany', data.name);

    setTimeout(() => {
      location.reload();
    }, 1000);
  };

  return (
    <div className={styles.radioList}>
      {companies.map((company, index) => {
        const displayName = `${
          company.name.charAt(0).toUpperCase() + company.name.slice(1)
        } ${company.original ? '' : 'Universal '}`;

        return (
          <label key={index} className={styles.radioLabel}>
            <input
              type="radio"
              name="company"
              value={displayName}
              className={styles.radioInput}
              checked={
                selectedCompany?.name === company.name &&
                selectedCompany.original === company.original
              }
              onChange={() =>
                handleSelect({ name: company.name, original: company.original })
              }
            />
            <span className={styles.displayName}>{displayName}</span>
          </label>
        );
      })}
    </div>
  );
};

const SideBarChooseCompanyCmp = () => {
  const [captchaSiteKey, setCaptchaSiteKey] = useState<string>(
    () =>
      (typeof window !== 'undefined' &&
        window.localStorage.getItem(CAPTCHA_DEV_SITE_KEY_STORAGE)) ||
      '',
  );

  const applyCaptchaSiteKey = (value: string) => {
    setCaptchaSiteKey(value);
    if (value) {
      window.localStorage.setItem(CAPTCHA_DEV_SITE_KEY_STORAGE, value);
    } else {
      window.localStorage.removeItem(CAPTCHA_DEV_SITE_KEY_STORAGE);
    }
    setTimeout(() => location.reload(), 300);
  };

  const [isOpen, setIsOpen] = useState(false);

  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };

  return (
    <div className={`${styles.sideMenuContainer} ${isOpen ? styles.open : ''}`}>
      <div className={styles.sideMenu}>
        <button className={styles.menuButton} onClick={toggleMenu}>
          {isOpen ? 'Close' : 'Open'}
        </button>
        <div className={styles.menuContent}>
          <input
            type="checkbox"
            onChange={() =>
              company.setIsActiveCaptchaDevMode(!company.isActiveCaptchaDevMode)
            }
            id="captcha"
            checked={company.isActiveCaptchaDevMode}
          />
          <label htmlFor="captcha">Enabled Captcha</label>

          {company.isActiveCaptchaDevMode && (
            <div className={styles.captchaKeyOverride}>
              <input
                type="text"
                value={captchaSiteKey}
                placeholder="Custom site key (optional)"
                onChange={(e) => setCaptchaSiteKey(e.target.value)}
                onBlur={(e) => applyCaptchaSiteKey(e.target.value)}
              />
              <div className={styles.captchaKeyHints}>
                <span>Cloudflare test keys:</span>
                {TURNSTILE_TEST_KEYS.map(({ key, label }) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => applyCaptchaSiteKey(key)}
                    className={styles.captchaKeyHint}
                  >
                    <code>{key}</code> — {label}
                  </button>
                ))}
              </div>
            </div>
          )}

          <RadioList companies={CompaniesMap} />
        </div>
      </div>
    </div>
  );
};

export default observer(SideBarChooseCompanyCmp);
